import React from 'react';

/**
 * Reusable DataTable component with heatmap gradient for numeric columns
 * @param {Array} data - Array of objects to display
 * @param {Array} columns - Array of column definitions with { key, label, type, format }
 * @param {String} title - Optional table title
 * @param {Number} rowsPerPage - Number of rows to display per page
 * @param {Boolean} showVisualBars - Enable heatmap gradient colors for numeric columns
 */
const DataTable = ({ 
  data = [], 
  columns = [], 
  title = '', 
  rowsPerPage = 100,
  showVisualBars = true
}) => {
  // Calculate max values for heatmap gradient scaling
  const getMaxValue = (key) => {
    return Math.max(...data.map(item => item[key] || 0));
  };

  // Format cell value based on column type
  const formatValue = (value, column) => {
    if (value === null || value === undefined) return '-';
    
    switch (column.type) {
      case 'percentage':
        // If value is already a decimal (0.6288), convert to percentage
        const percentValue = value < 1 ? value * 100 : value;
        return `${percentValue.toFixed(2)}%`;
      case 'number':
        return value.toLocaleString();
      case 'text':
      default:
        return value;
    }
  };

  // Chart palette colors
  const chartPalette = [
    { name: 'blue', hex: '#0072f0', rgb: [0, 114, 240] },      // Chart Palette 1
    { name: 'cyan', hex: '#00b6cb', rgb: [0, 182, 203] },      // Chart Palette 2
    { name: 'pink', hex: '#f10096', rgb: [241, 0, 150] },      // Chart Palette 3
    { name: 'orange', hex: '#f66d00', rgb: [246, 109, 0] },    // Chart Palette 4
    { name: 'yellow', hex: '#ffa800', rgb: [255, 168, 0] },    // Chart Palette 5
    { name: 'green', hex: '#7cb342', rgb: [124, 179, 66] }     // Chart Palette 6
  ];

  // Get heatmap color based on color index
  const getHeatmapColor = (value, key, colorIndex = 0) => {
    const maxValue = getMaxValue(key);
    const minValue = 0;
    
    // Get color palette (cycle through if more columns than colors)
    const paletteColor = chartPalette[colorIndex % chartPalette.length];
    
    if (value === 0 || maxValue === 0) {
      // Return very light version of the palette color for zero/empty
      const [r, g, b] = paletteColor.rgb;
      return `rgba(${r}, ${g}, ${b}, 0.1)`;
    }
    
    // Normalize value between 0 and 1
    const normalized = (value - minValue) / (maxValue - minValue);
    
    // Create gradient from light (0.3 opacity) to full color
    const [r, g, b] = paletteColor.rgb;
    const opacity = 0.3 + (normalized * 0.7); // Range from 0.3 to 1.0
    
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Get text color based on background (dark backgrounds need white text)
  const getTextColor = (value, key) => {
    const maxValue = getMaxValue(key);
    if (value === 0 || maxValue === 0) return '#333333';
    
    const normalized = value / maxValue;
    // Use white text for values above 60% of max
    return normalized > 0.6 ? '#ffffff' : '#333333';
  };

  // Calculate color index for each numeric/percentage column
  const getColorIndex = (columnIndex) => {
    // Count how many numeric/percentage columns come before this one
    let colorIndex = 0;
    for (let i = 0; i < columnIndex; i++) {
      if (columns[i].type === 'number' || columns[i].type === 'percentage') {
        colorIndex++;
      }
    }
    return colorIndex;
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {title && <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>}
        <div className="text-center text-gray-500 py-8">No data available</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {title && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                #
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.type === 'text' ? 'text-left' : 'text-right'
                  }`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.slice(0, rowsPerPage).map((row, index) => (
              <tr key={index} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {index + 1}.
                </td>
                {columns.map((column, columnIndex) => {
                  const value = row[column.key];
                  const isNumeric = column.type === 'number' || column.type === 'percentage';
                  const showHeatmap = showVisualBars && isNumeric;
                  
                  // Determine heatmap color based on column type
                  let backgroundColor = 'transparent';
                  let textColor = '#111827';
                  
                  if (showHeatmap) {
                    // Automatically assign color based on column order
                    const colorIndex = getColorIndex(columnIndex);
                    backgroundColor = getHeatmapColor(value, column.key, colorIndex);
                    textColor = getTextColor(value, column.key);
                  }
                  
                  return (
                    <td
                      key={column.key}
                      className={`px-6 py-4 text-sm font-medium transition-all hover:opacity-90 ${
                        column.type === 'text' ? 'text-left text-gray-900' : 'text-right whitespace-nowrap'
                      }`}
                      style={{
                        backgroundColor,
                        color: column.type === 'text' ? '#111827' : textColor
                      }}
                    >
                      {formatValue(value, column)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Footer with row count */}
      <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-500 text-right">
          Showing {Math.min(data.length, rowsPerPage)} of {data.length} rows
        </div>
      </div>
    </div>
  );
};

export default DataTable;

